import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { auth, db } from '../../firebase';
import { collection, getDocs } from 'firebase/firestore';

import SEO from '../SEO';
import Popup from '../Popup';
import ActTags from './ActTags'; // ActTags 컴포넌트 임포트
import Loading from '../Loading';
import MyPageMenu from './MyPageMenu';
import DeleteSelected from './DeleteSelected';

import PushPopup from './PushPopup'; // 제너럴 PushPopup 컴포넌트
import ImportActivityPopup from './ImportActivityPopup';

import styleClasses from './css/MyPage.module.scss';

function ActivityList() {
  const navigate  = useNavigate();

  const [uid, setUid]                 = useState(null);
  const [activities, setActivities]   = useState([]);
  const [isLoading, setIsLoading]     = useState(true);
  const [checkedIDs, setCheckedIDs]   = useState([]);

  // 팝업 관련 상태
  const [openPopup, setOpenPopup]             = useState(false);
  const [popupTitle, setPopupTitle]           = useState("");
  const [popupButtonText, setPopupButtonText] = useState("");
  const [popupFunction, setPopupFunction]     = useState(() => {});
  const [buttonLoading, setButtonLoading]     = useState(false);
  const [openPushActivityPopup, setOpenPushActivityPopup] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState([]);

  const [openImportActivitiesPopup, setOpenImportActivitiesPopup] = useState(false);

  // Firestore에서 /users_data/{uid}/activity 문서를 가져오기 위한 함수
  const fetchActivities = useCallback(async () => {
    try {
      if (!uid) { return; }
      const activitiesRef = collection(db, "users_data", uid, "activity");
      const snapshot = await getDocs(activitiesRef);
      if (snapshot.empty) {
        setActivities([]);
        setIsLoading(false);
        return;
      }
      const fetchedActivities = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setActivities(fetchedActivities);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching activities:", error);
      setIsLoading(false);
    }
  }, [uid]);

  // 현재 로그인된 사용자 UID 가져오기
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setIsLoading(true);
        setUid(user.uid);
      } else {
        navigate('/login');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (uid) {
      fetchActivities();
    }
  }, [fetchActivities, uid]);

  // 🔹 모든 액티비티 체크박스 선택/해제 처리
  const checkAllActivities = (event) => {
    let all = event.target.checked;
    const allCheckboxes = document.querySelectorAll('input[name=activity]');
    
    setCheckedIDs([]);
    allCheckboxes.forEach((checkbox) => {
      checkbox.checked = all;
      if (all) {
        setCheckedIDs((prev) => [...prev, checkbox.value]);
      }
    });
  };

  // 🔹 개별 액티비티 체크 처리
  const checkActivityID = (id) => {
    setCheckedIDs((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  // checkedIDs 또는 개별 id를 옵션으로 받을 수 있게 설정
  const openPushActivityMultiple = () => {
    if (checkedIDs.length) {
      setOpenPushActivityPopup(true);
    } else {
      setOpenPopup(true);
      setPopupTitle("활동을 선택해주세요.");
      setPopupButtonText("");
      setPopupFunction(() => {});
    }
  }

  // 개별 아이템을 직접 선택한 경우 (ActTags에서 넘어옴)
  useEffect(() => {
    if (selectedActivity.length) {
      setOpenPushActivityPopup(true);
    }
  }, [selectedActivity]);
  // const openPushActivity = (id) => {
  //   if (id) {
  //     setCheckedIDs([id]);
  //     setOpenPushActivityPopup(true);
  //   }
  // };

  // 채널 정보를 파싱하는 함수: "[YouTube] 카미트루" -> 아이콘 + "카미트루"
  const renderChannel = (channel) => {
    if (!channel) return '';
    const regex = /^\[(.*?)\]\s*(.*)$/;
    const match = channel.match(regex);
    if (match) {
      const type = match[1]; // 예: YouTube, Instagram, 기타
      const remainder = match[2];
      // YouTube와 Instagram은 아이콘 파일명이 소문자로 동일함.
      let iconFile = "";
      if (type === "YouTube" || type === "Instagram") {
        iconFile = `${type.toLowerCase()}-icon_1.png`;
      } else if (type === "기타") {
        iconFile = "etc-icon_1.png";
      }
      if (iconFile) {
        return (
          <span>
            <img src={`/assets/icons/${iconFile}`} alt={type}/>
            {remainder}
          </span>
        );
      }
    }
    return channel;
  };

  return (
    <>
      <SEO title='활동 목록' />
      <div className={styleClasses.myPage}>
        <div className={styleClasses.inner}>
          <div className={styleClasses.title}>
            <h1>Activity</h1>
            <div className="commonIcon toolTip">
              <i className="questionBlack w20"></i>
              <div className="toolTipText">
                <p>활동 기록 관리 공간 <br />유튜브, 인스타그램, 틱톡 등 여러 채널에 흩어져있는 <br />아티스트의 활동 기록을 한 번에 관리해보세요.</p>
              </div>
            </div>
          </div>
          <div className={styleClasses.editContent}>
            <div className={styleClasses.leftContent}>
              <MyPageMenu currentPage='activity' />
            </div>
            <div className={styleClasses.rightContent}>
              <div className={styleClasses.content}>
                <div className={styleClasses.myArchive}>
                  <div className={styleClasses.topButtons}>
                    <div className={`commonIcon toolTip ${ styleClasses.topButtonToolTip }`}>
                      <i className="questionBlack w20"></i>
                      <div className="toolTipText bottom">
                        <p>활동 기록을 자유롭게 추가, 수정, 삭제하세요. <br />그리고 원하는 기록만 골라서 뮤지카드에 적용해보세요!</p>
                      </div>
                    </div>
                    <DeleteSelected
                      selectedIDs={checkedIDs}
                      documents={activities} // 전체 Activity 배열
                      collectionPath={`users_data/${uid}/activity`}
                      userID={uid}
                      itemName="활동"
                      subcollections={[]} // 만약 활동 문서에 하위 컬렉션이 있다면 여기에 이름 배열을 넣습니다.
                      onDeleteSuccess={() => setCheckedIDs([])}
                      fetchData={fetchActivities}
                      setOpenPopup={ setOpenPopup }
                      setButtonLoading={ setButtonLoading }
                      setPopupTitle={ setPopupTitle }
                      setPopupFunction={ setPopupFunction }
                      setPopupButtonText={ setPopupButtonText }
                    />
                    <div className="commonButton fullwidth">
                      <Link to={`/my-page/activity/add`} className='black'>추가</Link>
                    </div>
                    <div className={`commonButton fullwidth ${ styleClasses.lastButton }`}>
                    {/*<Link to={`/my-page/activity/push?activityIdT=${checkedIDs.join(',')}`} className='black'>뮤지카드적용</Link>*/}
                    <button className="black" onClick={ () => { openPushActivityMultiple(); setSelectedActivity([]); } }>뮤지카드 적용</button>
                    </div>
                    <div className={`commonButton fullwidth ${ styleClasses.lastButton }`}>
                      <button className="black" onClick={ () => setOpenImportActivitiesPopup(true) }>활동 가져오기</button>
                    </div>
                  </div>
                  <div className={styleClasses.activityList}>
                    <table>
                      <thead>
                        <tr>
                          <th>
                            <div className='commonForm'>
                              <div className="formRow">
                                <div className="fieldBox checkBox">
                                  <input
                                    type="checkbox"
                                    id='activityAll'
                                    name='activityAll'
                                    onChange={checkAllActivities}
                                  />
                                </div>
                              </div>
                            </div>
                          </th>
                          <th>활동 타이틀</th>
                          <th>채널</th>
                          <th>뮤지카드</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isLoading ? (
                          <tr>
                            <td colSpan="4">
                              <Loading />
                            </td>
                          </tr>
                        ) : (
                          activities.map((activity, index) => (
                            <tr key={activity.id} className={styleClasses.eachRow}>
                              <td className={styleClasses.checkBox}>
                                <div className='commonForm'>
                                  <div className="formRow">
                                    <div className="fieldBox checkBox">
                                      <input
                                        type="checkbox"
                                        id={`activity${index}`}
                                        name='activity'
                                        value={activity.id}
                                        checked={checkedIDs.includes(activity.id)}
                                        onChange={(e) => checkActivityID(e.target.value)}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <Link to={`/my-page/activity/${activity.id}`} className={`black ${ styleClasses.linkWrap }`}>
                                  <img 
                                    src={
                                      activity.photo 
                                        ? activity.photo 
                                        : activity.videoId 
                                        ? `https://img.youtube.com/vi/${activity.videoId}/0.jpg` 
                                        : '/assets/images/no_image.svg'
                                    }
                                    alt={activity.title || activity.name}
                                    onError={(e) => {
                                      e.target.onerror = null; // 무한루프 방지
                                      e.target.src = '/assets/images/image_error.svg';
                                    }}
                                  />
                                  <div>
                                    { activity.title }
                                  </div>
                                </Link>
                              </td>
                              <td>{renderChannel(activity.channel)}</td>
                              <td>
                              <ActTags
                                act={activity.act}
                                userUid={uid}
                                attribute="activity"
                                albumID={activity.id}
                                fetchData={fetchActivities}
                                setSelectedItem={setSelectedActivity}  // 변경: setSelectedItem로 수정
                                setOpenPopup={setOpenPopup}
                                setButtonLoading={setButtonLoading}
                                setPopupTitle={setPopupTitle}
                                setPopupFunction={setPopupFunction}
                                setPopupButtonText={setPopupButtonText}
                                // openPushPopup={ openPushActivity } // ✅ 이 함수 추가
                              />
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* 기존 PushAlbumPopup 대신 제너럴 PushPopup 사용 */}
      <PushPopup
        open={ openPushActivityPopup }
        setOpenPopup={ setOpenPushActivityPopup }
        attribute="activity"
        selectedItem={ selectedActivity }       // 단일 선택
        setSelectedItem={ setSelectedActivity } // 단일 선택 상태 초기화
        selectedItems={ checkedIDs }            // 다중 선택
        clearSelectedItems={ setCheckedIDs }    // 다중 선택 상태 초기화
        categoryOptions={[
          { value: 'stages', label: '공연목록 (Performance List)' },
          { value: 'events', label: '행사목록 (Events List)' }
        ]}
        defaultCategory="stages"
        fetchData={ fetchActivities }
        setOpenAlertPopup={ setOpenPopup }
        setPopupTitle={ setPopupTitle }
        setPopupButtonText={ setPopupButtonText }
        setPopupFunction={ setPopupFunction }
      />
      <ImportActivityPopup
        open={ openImportActivitiesPopup }
        setOpenPopup={ setOpenImportActivitiesPopup }
        fetchActivities={ fetchActivities }
      />
      <Popup
        open={openPopup}
        setOpenPopup={setOpenPopup}
        title={popupTitle}
        text=""
        buttonText={popupButtonText}
        buttonFunction={popupFunction}
        buttonLoading={buttonLoading}
      />
    </>
  );
}

export default ActivityList;